// @ts-check
import { acceptHMRUpdate, defineStore } from 'pinia';

/** @import { PuestoTrabajo, Trabajador } from '@erpagroapp/types' */

export const useRepairPartsStore = defineStore('repairParts', {
  state: () => ({
    /**
     * Stores worker's data.
     * @type {Trabajador | null}
     */
    worker: null,

    /**
     * Stores user's work positions received in `Login()`.
     * @type {PuestoTrabajo[] | null}
     */
    workPositions: null,

    /**
     * List of work parts.
     * @type {WorkPartItem[] | null}
     */
    workParts: null,

    /** Whether the work parts are being loaded. */
    isLoadingWorkParts: false,

    /**
     * The date user selected to search for work parts.
     * @type {{ Init: Date, End: Date } | null}
     */
    selectedDate: null,

    /**
     * The current tab index.
     * @type {number}
     */
    currentTab: 0,

    /**
     * The work part details.
     * @type {WorkPartDetails | null}
     */
    workPartDetails: null,
  }),

  persist: {
    paths: [],
  },
});

if (import.meta.webpackHot) {
  import.meta.webpackHot.accept(acceptHMRUpdate(useRepairPartsStore, import.meta.webpackHot));
}

/**
 * @typedef {object} WorkPartItem
 * @property {number} Id_ParteProduccion - The ID of the production part.
 * @property {number} Id_OrdenProduccion - The ID of the production order.
 * @property {number} Id_ParteProduccionEstado - The ID of the production part state.
 * @property {string} Codigo_ParteProduccionEstado - The work part state code.
 * @property {string} Nombre_ParteProduccionEstado - The work part state name.
 * @property {string} Color_ParteProduccionEstado - The work part state color in hex code.
 * @property {string} Fecha - The date of the production part.
 * @property {string} Serie - The series of the production part.
 * @property {number} Numero - The number of the production part.
 * @property {string} NombreArticulo - The name of the article.
 * @property {number} Prioridad - The priority of the production part.
 * @property {string} ObservacionesOrden - Observations related to the production order.
 * @property {string | null} FechaInicio - The start date of the production part, if available.
 * @property {string | null} FechaFinalizacionOrden - The date of completion, if available.
 * @property {string} NombreTrabajadorCompleto - The full name of the worker associated with the production part.
 * @property {string} NombreCliente - The name of the client.
 * @property {string | null} DireccionClienteCompleta - The complete address of the client, if available.
 * @property {string} NumeroSerie - The serial number of the production part.
 * @property {number | null} Latitud - The latitude information, if available.
 * @property {number | null} Longitud - The longitude information, if available.
 * @property {number | null} Distancia - The distance, if applicable.
 * @property {string | null} FirmaParte - The signature associated with the production part.
 * @property {string | null} FirmaAlbaran - The signature associated with the delivery note.
 * @property {number} Id_TrabajadorPropietario - The ID of the owning worker.
 * @property {number} NumeroTrabajadoresActivos - The number of active workers.
 * @property {number} NumeroTrabajadoresPrevistos - The number of expected workers.
 * @property {number} TrabajosEnDomicilio - Indicates if there are jobs at the client's location.
 * @property {number} TieneIntervencionActiva - Indicates if there is an active intervention.
 */

/**
 * @typedef {object} WorkPartDetails
 * @property {number} Id_ParteProduccion - The ID of the work part.
 * @property {number} Id_OrdenProduccion - The ID of the production order.
 * @property {number} Id_ParteProduccionEstado - The ID of the production part state.
 * @property {string} Codigo_ParteProduccionEstado - The work part state code.
 * @property {string} Nombre_ParteProduccionEstado - The work part state name.
 * @property {string} Color_ParteProduccionEstado - The work part state color in hex code.
 * @property {string} FechaInicio - The start date of the work part.
 * @property {string} SerieParte - The part serial number.
 * @property {number} NumeroParte - The part number.
 * @property {string} ObservacionesParte - The part observations.
 * @property {number} Prioridad - The priority of the production part.
 * @property {string} SerieOrden - The production order serial number.
 * @property {number} NumeroOrden - The production order number.
 * @property {string | null} FechaFinalizacionOrden - The date of completion, if available.
 * @property {string} ObservacionesOrden - The production order observations.
 * @property {number} Id_Articulo - The ID of the article.
 * @property {string} NombreArticulo - The article name.
 * @property {string} NombreCliente - The client name.
 * @property {string} NumeroSerie - The serial number.
 * @property {number} Id_TrabajadorPropietario - The ID of the responsible worker of the part.
 * @property {number} NumeroTrabajadoresActivos - The number of active workers.
 * @property {number} NumeroTrabajadoresPrevistos - The number of planned workers.
 * @property {number} TrabajosEnDomicilio - Whether the work is at client site.
 * @property {number} TieneIntervencionActiva - Indicates if there is an active intervention.
 */
